// src/utils/macroFormatters.ts
/**
 * Macro Indicator Formatting Utilities
 *
 * Provides consistent formatting for macro economic indicators across
 * the MacroKeyMetricsWidget, Overview tables, and Economy Indicators tables.
 */

// Indicator metadata for proper formatting
interface IndicatorMeta {
  name: string;
  decimals: number;
  prefix?: string;
  suffix?: string;
  noScale?: boolean; // If true, don't auto-scale (show full number)
  multiplier?: number; // Multiply raw value by this (e.g., data in millions -> multiply by 1M)
}

/**
 * Metadata for known macro indicators defining their display format.
 * Keys are indicator_code values from the database.
 */
export const INDICATOR_META: Record<string, IndicatorMeta> = {
  // Crypto - show full price with $ prefix
  BTC_USD: { name: "Bitcoin", decimals: 2, prefix: "$", noScale: true },
  // Market Risk & Volatility (index - show full value)
  DXY: { name: "DXY", decimals: 2, noScale: true },
  VIX: { name: "VIX", decimals: 2, noScale: true },
  // Equities (index - show full value)
  SP500: { name: "S&P 500", decimals: 2, noScale: true },
  NASDAQ: { name: "Nasdaq", decimals: 2, noScale: true },
  // Commodities ($)
  GOLD_FUTURES_USD: { name: "Gold", decimals: 2, prefix: "$", noScale: true },
  OIL_WTI: { name: "Oil (WTI)", decimals: 2, prefix: "$", noScale: true },
  // Fed & Liquidity ($) - auto-scaled (data is in millions, multiply by 1M)
  US_CB_BALANCE_SHEET: {
    name: "Fed Balance",
    decimals: 2,
    prefix: "$",
    multiplier: 1_000_000,
  },
  US_M2_MONEY_SUPPLY: {
    name: "US M2 Money Supply",
    decimals: 2,
    prefix: "$",
    multiplier: 1_000_000,
  },
  US_NET_LIQUIDITY: {
    name: "US Net Liquidity",
    decimals: 2,
    prefix: "$",
    multiplier: 1_000_000,
  },
  US_FED_FUNDS_EFFECTIVE: { name: "Fed Funds", decimals: 2, suffix: "%" },
  US_CB_LIQUIDITY_SWAPS: {
    name: "CB Liquidity Swaps",
    decimals: 2,
    prefix: "$",
    multiplier: 1_000_000,
  },
  // Treasuries (%)
  US_TREASURY_YIELD_2Y: { name: "2Y Yield", decimals: 2, suffix: "%" },
  US_TREASURY_YIELD_10Y: { name: "10Y Yield", decimals: 2, suffix: "%" },
  US_TREASURY_YIELD_30Y: { name: "30Y Yield", decimals: 2, suffix: "%" },
  // Credit (percentage)
  US_OAS: { name: "BofA OAS", decimals: 2, suffix: "%" },
  // Inflation
  US_CPI: { name: "US CPI", decimals: 1, suffix: "%" },
  EUROZONE_CPI: { name: "EU CPI", decimals: 1, noScale: true },
};

/**
 * Format number with at least N significant figures
 */
export const formatWithSignificantFigures = (
  value: number,
  minSigFigs: number = 3
): string => {
  if (value === 0) return "0";

  const absValue = Math.abs(value);

  // For values >= 1, use standard decimal formatting
  if (absValue >= 1) {
    // Determine decimal places needed for sig figs
    const intDigits = Math.floor(Math.log10(absValue)) + 1;
    const decimals = Math.max(0, minSigFigs - intDigits);
    return value.toLocaleString(undefined, {
      minimumFractionDigits: Math.min(decimals, 2),
      maximumFractionDigits: Math.min(decimals, 2),
    });
  }

  // For values < 1, need more decimal places
  const magnitude = Math.floor(Math.log10(absValue));
  const decimals = Math.abs(magnitude) + minSigFigs - 1;
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Smart auto-scaling for large numbers (K, M, B, T)
 */
export const autoScaleValue = (
  value: number
): { scaled: number; suffix: string } => {
  const absValue = Math.abs(value);

  if (absValue >= 1_000_000_000_000) {
    return { scaled: value / 1_000_000_000_000, suffix: "T" };
  }
  if (absValue >= 1_000_000_000) {
    return { scaled: value / 1_000_000_000, suffix: "B" };
  }
  if (absValue >= 1_000_000) {
    return { scaled: value / 1_000_000, suffix: "M" };
  }
  if (absValue >= 1_000) {
    return { scaled: value / 1_000, suffix: "K" };
  }
  return { scaled: value, suffix: "" };
};

/**
 * Format a macro indicator value with proper units and precision.
 *
 * @param value - The raw numeric value
 * @param indicatorCode - The indicator_code from the database (e.g., "DXY", "US_CPI")
 * @returns Formatted string with proper prefix/suffix and scaling
 */
export const formatMacroValue = (
  value: number | null | undefined,
  indicatorCode: string
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }

  const meta = INDICATOR_META[indicatorCode];
  if (!meta) {
    // Unknown indicator: use default auto-scaling
    const { scaled, suffix } = autoScaleValue(value);
    return formatWithSignificantFigures(scaled, 3) + suffix;
  }

  // Apply multiplier if specified (e.g., data in millions -> actual value)
  let actualValue = value;
  if (meta.multiplier) {
    actualValue = value * meta.multiplier;
  }

  // For values with suffix (%, etc.), just format with specified decimals
  if (meta.suffix) {
    const formattedNumber = actualValue.toLocaleString(undefined, {
      minimumFractionDigits: meta.decimals,
      maximumFractionDigits: meta.decimals,
    });
    return `${meta.prefix || ""}${formattedNumber}${meta.suffix}`;
  }

  // For values that shouldn't be scaled (indices, small prices), show full number
  if (meta.noScale) {
    const formattedNumber = actualValue.toLocaleString(undefined, {
      minimumFractionDigits: meta.decimals,
      maximumFractionDigits: meta.decimals,
    });
    return `${meta.prefix || ""}${formattedNumber}`;
  }

  // For large values (Fed balance, M2, etc.), use smart auto-scaling
  const { scaled, suffix } = autoScaleValue(actualValue);
  // Always show 2 decimal places for scaled values for consistency
  const formattedNumber = scaled.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${meta.prefix || ""}${formattedNumber}${suffix}`;
};

/**
 * Get the display name for an indicator
 */
export const getIndicatorDisplayName = (indicatorCode: string): string => {
  return INDICATOR_META[indicatorCode]?.name || indicatorCode;
};

/**
 * Format a percentage change value
 */
export const formatMacroPercentChange = (
  value: number | null | undefined
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
};
